import { segment } from '@actions/analytics';
import {
  CHECKOUT_PRIME_STARTED,
  PRIME_PITCH_DISMISSED,
  PRIME_PITCH_DISPLAYED
} from '@cont/Checkout/constants/segment';
import { getPrimeGlobalProps } from '@stores/prime-quota/selectors';
import { savePrimeCheckoutContext } from '@cont/Checkout/utils';
import { PrimeTrialPromoContextualId } from './types';
import type { PrimePitchAnalyticsContext } from './types';

const sanitized = (value: unknown, fallback = 'NA') => {
  if (value === null || value === undefined || value === '') {
    return fallback;
  }
  return String(value);
};

const mapContextualFlowToPitchSource = (
  contextualFlow: PrimeTrialPromoContextualId | null
) => {
  switch (contextualFlow) {
    case PrimeTrialPromoContextualId.MAIN_SCREEN:
      return 'Home Screen';
    case PrimeTrialPromoContextualId.VIDEO:
      return 'Lesson Locked';
    case PrimeTrialPromoContextualId.TEST:
      return 'Test Locked';
    case PrimeTrialPromoContextualId.QUESTION_QUOTA:
    case PrimeTrialPromoContextualId.PYQ_QUOTA:
      return 'Practice Locked';
    case PrimeTrialPromoContextualId.COMPETE:
    case PrimeTrialPromoContextualId.COMPETE_EXHAUSTED:
      return 'Prime Sticky Bottomsheet';
    default:
      return 'Home Screen';
  }
};

const getPrimeCheckoutDefaults = () => ({
  business_platform: 'Unacademy',
  entity_content_type: 'Prime',
  entity_uid: 'NA',
  entity_title: 'NA',
  start_date: 'NA',
  end_date: 'NA'
});

const buildPrimePitchProps = ({
  contextualFlow,
  analyticsContext = {},
  subscriptionPrice
}: {
  contextualFlow: PrimeTrialPromoContextualId | null;
  analyticsContext?: PrimePitchAnalyticsContext;
  subscriptionPrice?: number | null;
}) => {
  const primePitchSource =
    analyticsContext.analyticsPitchSource ||
    mapContextualFlowToPitchSource(contextualFlow);

  const props: Record<string, unknown> = {
    prime_pitch_source: primePitchSource,
    lesson_uid:
      primePitchSource === 'Lesson Locked'
        ? sanitized(analyticsContext.lessonUid)
        : null,
    content_video_uid:
      primePitchSource === 'Lesson Locked'
        ? sanitized(analyticsContext.contentVideoUid)
        : null,
    test_uid:
      primePitchSource === 'Test Locked'
        ? sanitized(analyticsContext.testUid)
        : null,
    practice_uid:
      primePitchSource === 'Practice Locked'
        ? sanitized(analyticsContext.practiceUid)
        : null,
    ...getPrimeGlobalProps()
  };

  if (subscriptionPrice !== null && subscriptionPrice !== undefined) {
    const parsedPrice = Number(subscriptionPrice);
    if (!Number.isNaN(parsedPrice)) {
      props.subscription_price = parsedPrice;
    }
  }

  return props;
};

export const trackPrimePitchDisplayed = ({
  contextualFlow,
  analyticsContext,
  subscriptionPrice
}: {
  contextualFlow: PrimeTrialPromoContextualId | null;
  analyticsContext?: PrimePitchAnalyticsContext;
  subscriptionPrice?: number | null;
}) => {
  segment.track(
    PRIME_PITCH_DISPLAYED,
    buildPrimePitchProps({
      contextualFlow,
      analyticsContext,
      subscriptionPrice
    })
  );
};

export const trackPrimePitchDismissed = ({
  contextualFlow,
  analyticsContext,
  subscriptionPrice
}: {
  contextualFlow: PrimeTrialPromoContextualId | null;
  analyticsContext?: PrimePitchAnalyticsContext;
  subscriptionPrice?: number | null;
}) => {
  segment.track(
    PRIME_PITCH_DISMISSED,
    buildPrimePitchProps({
      contextualFlow,
      analyticsContext,
      subscriptionPrice
    })
  );
};

export const trackCheckoutPrimeStarted = ({
  contextualFlow,
  analyticsContext,
  goalUid,
  goalName,
  subscriptionPrice
}: {
  contextualFlow: PrimeTrialPromoContextualId | null;
  analyticsContext?: PrimePitchAnalyticsContext;
  goalUid?: string;
  goalName?: string;
  subscriptionPrice?: number | null;
}) => {
  const pitchProps = buildPrimePitchProps({
    contextualFlow,
    analyticsContext,
    subscriptionPrice
  });

  savePrimeCheckoutContext({
    prime_pitch_source: pitchProps.prime_pitch_source,
    subscription_price: pitchProps.subscription_price,
    goal_uid: goalUid,
    goal_name: goalName
  });

  segment.track(CHECKOUT_PRIME_STARTED, {
    ...getPrimeCheckoutDefaults(),
    purchase_type: 'New',
    subscription_type: 'Prime',
    is_combo: false,
    is_emi: false,
    is_referral_successful: false,
    referral_code: 'NA',
    is_subscription_active: false,
    is_upgrade: false,
    ...pitchProps,
    goal_uid: sanitized(goalUid),
    goal_name: sanitized(goalName)
  });
};
